import { useState, useCallback } from 'react'
import { useTranslation } from 'react-i18next'
import { useTelegram } from '../hooks/useTelegram'
import { calculateStars, formatStars, invokeStarsPayment } from '../lib/stars'

interface BuyTicketsProps {
  ticketPrice: string
  disabled?: boolean
  onSuccess?: (ticketNumbers: number[]) => void
}

const WEB_URL = 'https://hybridlotto.win'

export function BuyTickets({ disabled = false, onSuccess }: BuyTicketsProps) {
  const { t } = useTranslation()
  const { user, impactOccurred, notificationOccurred } = useTelegram()
  const [ticketCount, setTicketCount] = useState(1)
  const [isLoading, setIsLoading] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const [success, setSuccess] = useState(false)

  const stars = calculateStars(ticketCount)
  const maxTickets = 10

  const handleIncrement = useCallback(() => {
    if (ticketCount < maxTickets) {
      setTicketCount((prev) => prev + 1)
      impactOccurred('light')
    }
  }, [ticketCount, impactOccurred])

  const handleDecrement = useCallback(() => {
    if (ticketCount > 1) {
      setTicketCount((prev) => prev - 1)
      impactOccurred('light')
    }
  }, [ticketCount, impactOccurred])

  const handleBuy = async () => {
    if (disabled || isLoading) return
    setIsLoading(true)
    setError(null)
    impactOccurred('medium')
    try {
      const paymentResult = await invokeStarsPayment(ticketCount, user?.id || 0)
      if (!paymentResult.success) {
        throw new Error(paymentResult.error || 'Payment failed')
      }
      setSuccess(true)
      notificationOccurred('success')
      onSuccess?.([])
      setTimeout(() => { setSuccess(false); setTicketCount(1) }, 3000)
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Purchase failed')
      notificationOccurred('error')
    } finally {
      setIsLoading(false)
    }
  }

  const handleOpenInBrowser = () => {
    impactOccurred('light')
    const tg = window.Telegram?.WebApp
    if (tg && typeof tg.openLink === 'function') {
      tg.openLink(WEB_URL)
    } else {
      window.open(WEB_URL, '_blank', 'noopener,noreferrer')
    }
  }

  if (success) {
    return (
      <div className="card text-center py-8">
        <div className="text-5xl mb-3">🎉</div>
        <div className="text-xl font-semibold text-gold-500">
          {t('tickets.purchaseSuccess', { count: ticketCount })}
        </div>
        <div className="text-telegram-hint mt-2">{t('tickets.goodLuck')}</div>
      </div>
    )
  }

  return (
    <div className="card">
      <h3 className="text-sm font-medium text-telegram-hint mb-4">{t('tickets.buy')}</h3>

      <div className="flex items-center justify-center gap-6 mb-6">
        <button onClick={handleDecrement} disabled={ticketCount <= 1 || isLoading} className="number-btn text-2xl text-gold-500 disabled:opacity-30">−</button>
        <div className="text-center">
          <div className="text-5xl font-bold text-telegram-text">{ticketCount}</div>
          <div className="text-sm text-telegram-hint mt-1">{ticketCount === 1 ? 'ticket' : 'tickets'}</div>
        </div>
        <button onClick={handleIncrement} disabled={ticketCount >= maxTickets || isLoading} className="number-btn text-2xl text-gold-500 disabled:opacity-30">+</button>
      </div>

      <div className="text-center mb-2">
        <span className="text-2xl font-semibold text-gold-400">{formatStars(stars)}</span>
      </div>
      <div className="text-center text-xs text-telegram-hint mb-4">
        {t('tickets.starsPriceHint')}
      </div>

      {error && <div className="text-red-400 text-sm text-center mb-4">{error}</div>}

      <button onClick={handleBuy} disabled={disabled || isLoading} className="btn-gold w-full flex items-center justify-center gap-2">
        {isLoading ? <><span className="animate-spin">⏳</span>Processing...</> : <>🎫 {t('tickets.buy')} — {formatStars(stars)}</>}
      </button>

      <div className="flex items-center gap-3 my-4">
        <div className="flex-1 h-px bg-telegram-secondary" />
        <span className="text-xs text-telegram-hint uppercase">{t('tickets.orDivider')}</span>
        <div className="flex-1 h-px bg-telegram-secondary" />
      </div>

      <button onClick={handleOpenInBrowser} disabled={isLoading} className="w-full py-3 rounded-lg border border-telegram-secondary text-telegram-text hover:border-gold-500 transition-colors flex flex-col items-center justify-center gap-1">
        <span className="text-sm font-medium">💳 {t('tickets.payInBrowser')}</span>
        <span className="text-xs text-telegram-hint">{t('tickets.payInBrowserHint')}</span>
      </button>

      <div className="mt-4 text-xs text-telegram-hint text-center leading-relaxed">
        ℹ️ {t('tickets.equalOddsNote')}
      </div>

      <div className="mt-4 pt-4 border-t border-telegram-secondary">
        <div className="text-xs text-telegram-hint text-center">{t('prizes.distribution')}: 🥇 65% • 🥈 25% • 🥉 10%</div>
      </div>
    </div>
  )
}
